import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { hasDevToken, AwxJobRow, AwxJobStats, AwxJobListResponse, AwxJobEvent } from '../services/api';
// Welle 4.6 QoL-6: shared German status labels.
import { labelForAwxJobStatus } from '../services/status-labels';

// AWX terminal-status colour map. Mirrors the AWX UI itself so an
// operator who jumps between the SecTepe console and the AWX UI
// sees the same palette. Non-terminal values (waiting / running /
// pending — bus-gateway forwards them all) bucket into the same
// "in flight" amber bin.
const STATUS_COLORS: Record<string, string> = {
  successful: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-700',
  error: 'bg-red-100 text-red-700',
  canceled: 'bg-slate-200 text-slate-700',
  pending: 'bg-amber-100 text-amber-800',
  waiting: 'bg-amber-100 text-amber-800',
  running: 'bg-blue-100 text-blue-800',
  new: 'bg-blue-100 text-blue-800',
};

type StatusFilter = 'all' | 'pending' | 'finished';

// Polling fallback when SSE isn't available (e.g. some corporate
// proxies strip text/event-stream). 5s is a sensible upper bound
// for "real-time-ish" without hammering the backend.
const POLL_INTERVAL_MS = 5_000;

// Maximum reconnect backoff for the SSE stream. The browser's
// EventSource auto-reconnects but with a vendor-defined base; we
// don't override its scheduling — only cap our reconnect-attempts
// counter so the UI surfaces a "stream stalled" hint if it churns.
const SSE_RECONNECT_HINT_AFTER = 3;

export function AwxJobsPage() {
  const [rows, setRows] = useState<AwxJobRow[]>([]);
  const [stats, setStats] = useState<AwxJobStats>({ total: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<AwxJobRow | null>(null);
  const [streamState, setStreamState] = useState<'connecting' | 'live' | 'polling'>('connecting');
  const reconnectCountRef = useRef(0);

  const refresh = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    return api.get<AwxJobListResponse>('/awx-jobs', { params: { status: filter, limit: 200 } })
      .then((r) => {
        setRows(r.data.rows);
        setStats(r.data.stats);
      })
      .finally(() => { if (!opts?.silent) setLoading(false); });
  }, [filter]);

  // Initial + filter-change load.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live updates. Prefer SSE; fall back to a 5s poll if EventSource
  // is unavailable (older browsers) or if the connection errors
  // repeatedly. The EventSource transport sends the bearer via the
  // existing cookie (oauth2-proxy) — when running in dev with a
  // ?token=… bearer, EventSource can't set Authorization headers,
  // so we surface that with the polling fallback. The fallback
  // also handles SSE-stripping proxies transparently.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      setStreamState('polling');
      const id = window.setInterval(() => { refresh({ silent: true }); }, POLL_INTERVAL_MS);
      return () => window.clearInterval(id);
    }

    // In dev (in-memory ?token= bearer), EventSource can't authenticate.
    // We detect that via hasDevToken() and short-circuit to polling. In production, the oauth2-proxy cookie
    // is sent automatically and SSE works.
    if (hasDevToken()) {
      setStreamState('polling');
      const id = window.setInterval(() => { refresh({ silent: true }); }, POLL_INTERVAL_MS);
      return () => window.clearInterval(id);
    }

    const es = new EventSource('/api/awx-jobs/stream');
    es.addEventListener('hello', () => {
      setStreamState('live');
      reconnectCountRef.current = 0;
    });
    es.addEventListener('awx.job', (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent<string>).data) as AwxJobEvent;
        // Splice the updated job into the table without a full
        // refetch — the SSE payload mirrors the list-endpoint row
        // shape minus the payload/result blobs. We keep the
        // existing payload/result on the previously-loaded row so
        // the side-panel doesn't lose detail mid-update.
        setRows((prev) => {
          const idx = prev.findIndex((r) => r.id === data.jobId);
          if (idx === -1) {
            // Job not in the current view — only useful if it
            // matches the active filter. Trigger a silent refetch
            // so totals + pagination stay coherent.
            refresh({ silent: true });
            return prev;
          }
          const merged: AwxJobRow = {
            ...prev[idx],
            template: data.template,
            status: data.status,
            startedAt: data.startedAt,
            finishedAt: data.finishedAt,
            tenantSlug: data.tenantSlug,
            tenantName: data.tenantName,
          };
          const next = prev.slice();
          next[idx] = merged;
          return next;
        });
        // Stats matter for the header card — recompute from the
        // freshly-known status of this job + the current view.
        // Cheap, no round-trip.
        setStats((prev) => deriveStats(prev, data.status));
      } catch {
        // Malformed event — fall back to a full refetch so the UI
        // doesn't drift out of sync.
        refresh({ silent: true });
      }
    });
    es.onerror = () => {
      reconnectCountRef.current += 1;
      if (reconnectCountRef.current >= SSE_RECONNECT_HINT_AFTER) {
        setStreamState('polling');
      }
    };
    return () => { es.close(); };
  }, [refresh]);

  // Polling-mode safety net: when SSE is unavailable we additionally
  // schedule a silent refetch on the same cadence. Effect runs
  // separately from the SSE wiring so the cleanup is independent.
  useEffect(() => {
    if (streamState !== 'polling') return;
    const id = window.setInterval(() => { refresh({ silent: true }); }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [streamState, refresh]);

  return (
    <div className="relative">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">AWX-Jobs</h2>
          <p className="text-sm text-slate-500">
            Cross-Tenant Lifecycle-Jobs (Provisionierung, Suspend, Plan-Change,
            Decommission). Status-Updates live via SSE; Fallback alle 5 s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StreamBadge state={streamState} />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-2 py-1 rounded border"
          >
            <option value="all">Alle</option>
            <option value="pending">Laufend</option>
            <option value="finished">Beendet</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Total" value={stats.total} tone="slate" />
        <StatCard label="Laufend" value={stats.pending} tone="amber" />
        <StatCard label="Failed" value={stats.failed} tone="red" />
      </div>

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left uppercase tracking-wide">
            <tr>
              <th className="p-3">Tenant</th>
              <th className="p-3">Template</th>
              <th className="p-3">Status</th>
              <th className="p-3">Gestartet</th>
              <th className="p-3">Beendet</th>
              <th className="p-3">AWX-Job-ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((j) => (
              <tr
                key={j.id}
                onClick={() => setSelected(j)}
                className={`border-t cursor-pointer hover:bg-slate-50 ${selected?.id === j.id ? 'bg-slate-50' : ''}`}
              >
                <td className="p-3">
                  <Link
                    to={`/tenants/${j.tenantSlug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-primary-600 hover:underline font-mono"
                  >
                    {j.tenantSlug}
                  </Link>
                  <span className="text-slate-500 ml-2">{j.tenantName}</span>
                </td>
                <td className="p-3 font-mono text-xs">{j.template}</td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[j.status] ?? 'bg-slate-200 text-slate-700'}`}>
                    {labelForAwxJobStatus(j.status)}
                  </span>
                </td>
                <td className="p-3 text-slate-500">{fmtDate(j.startedAt)}</td>
                <td className="p-3 text-slate-500">{fmtDate(j.finishedAt)}</td>
                <td className="p-3 font-mono">{j.id}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">
                Keine AWX-Jobs für diesen Filter.
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {selected && (
        <JobDetailPanel job={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  tone: 'slate' | 'amber' | 'red';
}

function StatCard({ label, value, tone }: StatCardProps) {
  const toneClass = {
    slate: 'text-slate-800',
    amber: 'text-amber-700',
    red: 'text-red-700',
  }[tone];
  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-3xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function StreamBadge({ state }: { state: 'connecting' | 'live' | 'polling' }) {
  const map = {
    connecting: { label: 'connecting…', cls: 'bg-slate-100 text-slate-600' },
    live: { label: '● live', cls: 'bg-green-100 text-green-800' },
    polling: { label: 'polling (5s)', cls: 'bg-amber-100 text-amber-800' },
  } as const;
  return <span className={`badge ${map[state].cls}`}>{map[state].label}</span>;
}

interface JobDetailPanelProps {
  job: AwxJobRow;
  onClose: () => void;
}

function JobDetailPanel({ job, onClose }: JobDetailPanelProps) {
  return (
    <div className="fixed top-0 right-0 w-full max-w-xl h-full bg-white shadow-2xl border-l border-slate-200 overflow-y-auto z-50">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <h3 className="font-semibold">AWX-Job {job.id}</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-2xl leading-none px-2">
          ×
        </button>
      </div>
      <div className="p-5 space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tenant">
            <Link to={`/tenants/${job.tenantSlug}`} className="text-primary-600 hover:underline font-mono">
              {job.tenantSlug}
            </Link>
            <span className="text-slate-500 block">{job.tenantName}</span>
          </Field>
          <Field label="Status">
            <span className={`badge ${STATUS_COLORS[job.status] ?? 'bg-slate-200 text-slate-700'}`}>
              {labelForAwxJobStatus(job.status)}
            </span>
          </Field>
          <Field label="Template"><code className="font-mono text-xs">{job.template}</code></Field>
          <Field label="AWX-Job-ID"><code className="font-mono">{job.id}</code></Field>
          <Field label="Gestartet">{fmtDate(job.startedAt)}</Field>
          <Field label="Beendet">{fmtDate(job.finishedAt)}</Field>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Payload</p>
          <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-x-auto">
            {JSON.stringify(job.payload, null, 2)}
          </pre>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Result</p>
          <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-x-auto">
            {job.result === null || job.result === undefined ? '—' : JSON.stringify(job.result, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * Incrementally update the header stats when an SSE event arrives.
 *
 * The stats card cares about three numbers — total, in-flight, and
 * failed. A new job event always means an existing row flipped,
 * so `total` stays put. The pending/failed buckets shift based on
 * whether the new status is terminal and whether it is the failure
 * bucket. Worst case the numbers drift by ±1 between an SSE event
 * and the next periodic refetch; an operator's next filter-change
 * triggers a full re-query, so drift is self-healing.
 */
function deriveStats(prev: AwxJobStats, newStatus: string): AwxJobStats {
  const isTerminal = newStatus === 'successful' || newStatus === 'failed';
  // We can't tell from the SSE frame alone whether the job WAS
  // already finished. Conservative deltas:
  //   - if new status is failed → +1 failed
  //   - if new status is terminal → decrement pending (clamped)
  //   - if new status is non-terminal → it became (or remained)
  //     in-flight; leave pending alone to avoid double-counting.
  return {
    total: prev.total,
    pending: isTerminal ? Math.max(0, prev.pending - 1) : prev.pending,
    failed: newStatus === 'failed' ? prev.failed + 1 : prev.failed,
  };
}
